import { useEffect, useRef } from 'react';

import { extractArtifactInputsFromMessages } from '@erikdakoda/artifact/shared/extractArtifactInputsFromMessages';
import type { AiMessageForUi } from '@erikdakoda/convo/types';
import { doFetch } from '@erikdakoda/ui/shared/doFetch';
import { buildArtifactInputsSyncKey } from '../shared/syncArtifactStoreFromMessageInputs';
import { useArtifactsSidebarStore } from './useArtifactsSidebarStore';

type UseSyncArtifactsFromConvoMessagesParams = {
  convoId: string | undefined;
  messages: readonly AiMessageForUi[];
  enabled?: boolean;
};

/**
 * Rehydrates missing artifact DB rows from convo message tool parts and refreshes the sidebar.
 */
export function useSyncArtifactsFromConvoMessages({
  convoId,
  messages,
  enabled = true,
}: UseSyncArtifactsFromConvoMessagesParams): void {
  const bumpArtifactsRefresh = useArtifactsSidebarStore((state) => state.bumpArtifactsRefresh);
  const syncedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !convoId || messages.length === 0) {
      return;
    }

    const artifacts = extractArtifactInputsFromMessages(messages);
    if (artifacts.length === 0) {
      return;
    }

    const syncKey = `${convoId}:${buildArtifactInputsSyncKey(artifacts)}`;
    if (syncedKeyRef.current === syncKey) {
      return;
    }

    syncedKeyRef.current = syncKey;
    bumpArtifactsRefresh();

    void doFetch<{ createdCount: number }>({
      url: '/api/sync-artifacts-from-convo',
      method: 'POST',
      params: {
        convoId,
        artifacts,
      },
    })
      .then(() => {
        bumpArtifactsRefresh();
      })
      .catch(() => {
        syncedKeyRef.current = null;
      });
  }, [bumpArtifactsRefresh, convoId, enabled, messages]);
}
